import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { LayoutGrid, Star, ChevronRight, Store } from "lucide-react";

const WIDGETS = [
  { key: "product", label: "Product Widget", desc: "Shows entries earned beneath the Add to Cart button." },
  { key: "cart", label: "Cart Widget", desc: "Live entry calculator on the cart page." },
  { key: "checkout", label: "Checkout Widget", desc: "Celebration + entries earned on the Thank You page." },
];

export default function Widgets() {
  const [settings, setSettings] = useState(null);
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, g] = await Promise.all([
        api.get("/widgets/settings"),
        api.get("/giveaways"),
      ]);
      setSettings(s.data);
      setGiveaways(g.data);
    } catch {
      toast.error("Failed to load widget settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const featured = giveaways.find((g) => g.is_featured);

  const toggleWidget = async (key, enabled) => {
    setSettings((prev) => ({ ...prev, [key]: { ...prev[key], enabled } }));
    try {
      await api.put("/widgets/settings", { [key]: { enabled } });
      toast.success(`${WIDGETS.find((w) => w.key === key).label} ${enabled ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Could not save");
      setSettings((prev) => ({ ...prev, [key]: { ...prev[key], enabled: !enabled } }));
    }
  };

  const setFeatured = async (giveaway_id) => {
    try {
      await api.post(`/giveaways/${giveaway_id}/feature`);
      await load();
      toast.success("Featured giveaway updated");
    } catch {
      toast.error("Could not update featured giveaway");
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-8 py-10" data-testid="widgets-page">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
          <LayoutGrid className="size-3.5" strokeWidth={2} /> Storefront
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950 mt-2">Storefront Widgets</h1>
        <p className="text-sm text-zinc-500 mt-1">Show shoppers the entries they'll earn — on product pages, in the cart, and at checkout.</p>

        {/* Featured giveaway */}
        <div className="mt-8 bg-white border border-zinc-200 rounded-lg p-5" data-testid="featured-giveaway-card">
          <div className="flex items-center gap-2 mb-1">
            <Star className="size-4 text-amber-500" strokeWidth={2} fill="currentColor" />
            <h2 className="font-display font-bold text-zinc-950">Featured Giveaway</h2>
          </div>
          <p className="text-xs text-zinc-500 mb-4">Widgets use this giveaway's entry ratio. Only one can be featured at a time. If none is featured, all widgets hide automatically.</p>
          {loading ? (
            <div className="h-9 w-64 bg-zinc-100 rounded animate-pulse" />
          ) : giveaways.length === 0 ? (
            <p className="text-sm text-zinc-500">No giveaways yet. <Link to="/giveaways/new" className="text-zinc-950 underline">Create one</Link> to enable widgets.</p>
          ) : (
            <div className="space-y-2">
              {giveaways.map((g) => (
                <label key={g.giveaway_id} className="flex items-center gap-3 p-3 rounded-md border border-zinc-200 cursor-pointer hover:bg-zinc-50" data-testid={`feature-option-${g.giveaway_id}`}>
                  <input
                    type="radio"
                    name="featured"
                    checked={!!g.is_featured}
                    onChange={() => setFeatured(g.giveaway_id)}
                    className="accent-emerald-600 size-4"
                    data-testid={`feature-radio-${g.giveaway_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-950 truncate">{g.title}</div>
                    <div className="text-xs text-zinc-500">${g.entry_ratio_dollars} = {g.entry_ratio_entries} entries · {g.status}</div>
                  </div>
                  {g.is_featured && <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Featured</span>}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* App Status */}
        <div className="mt-8 bg-white border border-zinc-200 rounded-lg p-5" data-testid="app-status-card">
          <h2 className="font-display font-bold text-zinc-950 mb-1">App Status</h2>
          <p className="text-xs text-zinc-500 mb-4">Enable a widget here, then add its block in your Shopify Theme Editor.</p>

          {!loading && !featured && (
            <div className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2" data-testid="no-featured-warning">
              No featured giveaway selected — widgets are hidden on your storefront until you pick one above.
            </div>
          )}

          <div className="divide-y divide-zinc-100">
            {WIDGETS.map((w) => {
              const on = settings?.[w.key]?.enabled;
              return (
                <div key={w.key} className="flex items-center gap-4 py-4" data-testid={`widget-row-${w.key}`}>
                  <div className="size-10 rounded-md bg-zinc-100 flex items-center justify-center text-zinc-700">
                    <LayoutGrid className="size-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-950">{w.label}</span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${on ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`} data-testid={`widget-status-${w.key}`}>
                        {on ? "On" : "Off"}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{w.desc}</div>
                  </div>
                  <Switch
                    checked={!!on}
                    onCheckedChange={(v) => toggleWidget(w.key, v)}
                    disabled={loading}
                    data-testid={`widget-toggle-${w.key}`}
                  />
                  <Link to={`/widgets/${w.key}`}>
                    <Button variant="outline" size="sm" data-testid={`widget-settings-${w.key}`}>
                      Settings <ChevronRight className="size-3.5 ml-1" />
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* Install hint */}
        <div className="mt-6 bg-zinc-50 border border-zinc-200 rounded-lg p-5 flex gap-3" data-testid="install-hint">
          <Store className="size-5 text-zinc-500 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="text-sm text-zinc-600">
            <span className="font-medium text-zinc-950">No coding required.</span> After enabling a widget, open your Shopify admin → <span className="font-medium">Online Store → Themes → Customize</span>, then add the SweepLaunch <span className="font-medium">Product</span> / <span className="font-medium">Cart</span> block (or the <span className="font-medium">Thank-You</span> checkout block) and publish.
          </div>
        </div>
      </div>
    </Layout>
  );
}
